import React, { useRef, memo, useCallback, useMemo, useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { ArrowRight, Play } from 'lucide-react';
import { useInView, motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useIsMobile } from '@/hooks/use-mobile';
import { useStableCallback, useStableMemo } from '@/utils/performance';
import { useTheme } from 'next-themes';
import Monitor from './components/Monitor';

const fadeInUp = {
  hidden: { opacity: 0, y: 32 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] } }
};

const fadeInUpStagger = (delay = 0) => ({
  hidden: { opacity: 0, y: 32 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, delay, ease: [0.16, 1, 0.3, 1] } }
});


// AnimatedHeroCard component
// Fragmented Apps Animation Component
const FragmentedAppsAnimation = memo(({ isMobile }: { isMobile: boolean }) => {
  const { theme } = useTheme();
  const [currentAppIndex, setCurrentAppIndex] = useState(0);
  const [alertedApps, setAlertedApps] = useState<Set<number>>(new Set([0, 1])); // Start with 2 alerts

  const apps = useStableMemo(() => [
    { name: 'Trello', icon: '/trello.svg', color: '#0079BF' },
    { name: 'Jira', icon: '/jira.svg', color: '#0052CC' },
    { name: 'Slack', icon: '/slack.svg', color: '#4A154B' },
    { name: 'Gmail', icon: '/gmail.svg', color: '#EA4335' },
    { name: 'Calendar', icon: '/googlecalendar.svg', color: '#4285F4' },
    { name: 'Excel', icon: '/excel.svg', color: '#217346' },
    { name: 'WhatsApp', icon: '/whatsapp.svg', color: '#25D366' },
    { name: 'Salesforce', icon: '/salesforce.svg', color: '#00A1E0' },
    { name: 'Teams', icon: '/teams.svg', color: '#6264A7' },
    { name: 'Zoom', icon: '/zoom.svg', color: '#2D8CFF' },
    { name: 'Notion', icon: '/notion.svg', color: '#000000' },
    { name: 'Figma', icon: '/figma.svg', color: '#F24E1E' },
  ], [], 'fragmented-apps');

  // Perfectly synchronized alert cycling
  useEffect(() => {
    let cycleIndex = 0;

    const interval = setInterval(() => {
      setAlertedApps(currentAlerts => {
        if (currentAlerts.size === 0) return currentAlerts;

        const alertedArray = Array.from(currentAlerts);
        const appToShow = alertedArray[cycleIndex % alertedArray.length];

        // Update the current app index to show this alerted app
        setCurrentAppIndex(appToShow);

        // Create new alerts: remove current app, add new random app
        const newAlerts = new Set(currentAlerts);
        newAlerts.delete(appToShow); // Remove the app we're showing

        // Add alert to a random app that doesn't have one
        const availableApps = apps
          .map((_, i) => i)
          .filter(i => !newAlerts.has(i));

        if (availableApps.length > 0) {
          const randomApp = availableApps[Math.floor(Math.random() * availableApps.length)];
          newAlerts.add(randomApp);
        }

        cycleIndex++;
        return newAlerts;
      });
    }, 1500); // 1.5 second cycle for better visibility

    return () => clearInterval(interval);
  }, [apps.length]);

  const currentApp = apps[currentAppIndex];

  return (
    <div className={`relative ${isMobile ? 'w-80 h-80' : 'w-96 h-96'} flex flex-col items-center justify-center`}>
      {/* Notification icons at top - only 2 have alerts at any time */}
      <div className="absolute top-4 left-0 right-0 flex justify-center gap-2 flex-wrap px-4">
        {apps.map((app, index) => {
          const isActive = currentAppIndex === index;
          const hasAlert = alertedApps.has(index);

          return (
            <div
              key={app.name}
              className="relative"
            >
              <div
                className={`w-8 h-8 rounded-lg border backdrop-blur-sm flex items-center justify-center relative transition-all duration-300 ${isActive ? 'ring-2 ring-white/50 shadow-lg shadow-white/25 scale-110' : 'border-white/20'
                  }`}
                style={{
                  backgroundColor: `${app.color}15`,
                  borderColor: isActive ? '#ffffff' : `${app.color}40`,
                  boxShadow: isActive ? `0 0 20px ${app.color}50` : undefined
                }}
              >
                <img
                  src={app.icon}
                  alt={app.name}
                  className={`w-4 h-4 ${app.name === 'Zoom' ? (theme === 'dark' ? '' : 'filter invert') : (theme === 'dark' ? 'filter brightness-0 invert' : 'filter brightness-0')}`}
                />
                {/* Simple red notification badge with "1" - only for alerted apps, disappears when app becomes active */}
                {hasAlert && !isActive && (
                  <div className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full flex items-center justify-center border border-white">
                    <span className="text-white text-[6px] font-bold">1</span>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Computer monitor on stand */}
      <div className="relative mt-12">
        <Monitor
          width="w-36"
          height="h-24"
          animate={{
            boxShadow: currentApp ? `0 0 25px ${currentApp.color}50` : '0 0 10px rgba(0,0,0,0.3)'
          }}
        >
          {/* Screen content - cycling app */}
          <AnimatePresence mode="wait">
            <motion.div
              key={currentApp?.name || 'empty'}
              className="absolute inset-0 flex items-center justify-center"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              transition={{ duration: 0.8 }}
            >
              {currentApp ? (
                <div className="flex flex-col items-center">
                  <img
                    src={currentApp.icon}
                    alt={currentApp.name}
                    className={`w-10 h-10 ${currentApp.name === 'Zoom' ? (theme === 'dark' ? '' : 'filter invert') : (theme === 'dark' ? 'filter brightness-0 invert' : 'filter brightness-0')}`}
                  />
                  <div
                    className="text-white text-[9px] font-medium mt-1 px-2 py-0.5 rounded flex items-center gap-1"
                    style={{ backgroundColor: `${currentApp.color}80` }}
                  >
                    {currentApp.name}
                  </div>
                </div>
              ) : (
                <div className="text-gray-400 text-xs flex items-center gap-1">
                  Switching
                  <motion.div
                    className="w-0.5 h-3 bg-gray-400"
                    animate={{ opacity: [1, 0, 1] }}
                    transition={{ duration: 0.8, repeat: Infinity }}
                  />
                </div>
              )}
            </motion.div>
          </AnimatePresence>
        </Monitor>
      </div>
    </div>
  );
});

// Synkro Unified Features Component
const SynkroUnifiedFeatures = memo(({ isMobile }: { isMobile: boolean }) => {
  const { theme } = useTheme();
  return (
    <div className={`relative ${isMobile ? 'w-80 h-80' : 'w-96 h-96'} flex flex-col items-center justify-center`}>
      {/* Always glowing Synkro icon at top - shows unified solution */}
      <div className="absolute top-4 left-0 right-0 flex justify-center">
        <div className="relative">
          <div
            className="w-8 h-8 rounded-lg border backdrop-blur-sm flex items-center justify-center relative transition-all duration-300 ring-2 ring-white/50 shadow-lg shadow-white/25 scale-110"
            style={{
              backgroundColor: 'rgba(147, 51, 234, 0.15)', // purple background like other icons
              borderColor: '#ffffff',
              boxShadow: '0 0 20px rgba(147, 51, 234, 0.5)' // purple glow like other icons
            }}
          >
            <img
              src="/synkro.png"
              alt="Synkro"
              className={`w-6 h-6 ${theme === 'dark' ? 'filter brightness-0 invert' : 'filter brightness-0'}`}
            />
          </div>
        </div>
      </div>
      {/* Computer monitor on stand */}
      <div className="relative mt-12">
        <Monitor
          width="w-36"
          height="h-24"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5, duration: 0.8 }}
        >
          {/* Synkro on screen */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="flex flex-col items-center">
              <img
                src="/synkro.png"
                alt="Synkro"
                className={`w-10 h-10 ${theme === 'dark' ? 'filter brightness-0 invert' : 'filter brightness-0'}`}
              />
              <div className="text-white text-[9px] font-medium mt-1 px-2 py-0.5 rounded bg-gradient-to-r from-purple-600 to-blue-600 flex items-center gap-1">
                Synkro
              </div>
            </div>
          </div>
        </Monitor>
      </div>
    </div>
  );
});

// Isolated Collaboration Animation Component - 6 separate computers with ChatGPT
const IsolatedCollaborationAnimation = memo(({ isMobile }: { isMobile: boolean }) => {
  const { theme } = useTheme();

  return (
    <div className={`relative ${isMobile ? 'w-80 h-80' : 'w-96 h-96'} flex flex-col items-center justify-center`}>
      {/* 6 separate computer monitors with custom positioning */}
      <div className={`relative ${isMobile ? 'w-80 h-80' : 'w-96 h-96'} flex flex-col items-center justify-center`}>
        {/* Top row - aligned with Synkro icon */}
        <div className="absolute top-4 left-0 right-0 flex justify-center items-center gap-12">
          {Array.from({ length: 3 }).map((_, index) => (
            <React.Fragment key={`top-${index}`}>
              <div className="flex flex-col items-center relative">
                <Monitor
                  width="w-8"
                  height="h-6"
                  standWidthClass="w-2.5"
                  standHeightClass="h-1"
                  standNeckWidthClass="w-2"
                  standNeckHeightClass="h-0.5"
                  baseWidthClass="w-3.5"
                  baseHeightClass="h-1"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: index * 0.1, duration: 0.8 }}
                >
                  {/* ChatGPT on screen */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <img
                      src="/chatgpt.svg"
                      alt="ChatGPT"
                      className={`w-4 h-4 ${theme === 'dark' ? 'filter brightness-0 invert' : 'filter brightness-0'}`}
                    />
                  </div>
                </Monitor>
              </div>

              {/* Cubicle divider (except after last monitor) */}
              {index < 2 && (
                <div className="w-0.5 h-16 bg-gray-500/30 rounded-full mx-4"></div>
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Text between rows */}
        <div className="absolute top-28 left-0 right-0 flex justify-center">
          <div className="text-center">
            <p className="text-sm text-gray-500 dark:text-gray-400 font-medium">
              Different contexts for each user
            </p>
          </div>
        </div>

        {/* Bottom row - aligned with right panel monitors */}
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <div className="relative mt-2 flex justify-center items-center gap-12">
            {Array.from({ length: 3 }).map((_, index) => (
              <React.Fragment key={`bottom-${index}`}>
                <div className="flex flex-col items-center relative">
                  <Monitor
                    width="w-8"
                    height="h-6"
                    standWidthClass="w-2.5"
                    standHeightClass="h-1"
                    standNeckWidthClass="w-2"
                    standNeckHeightClass="h-0.5"
                    baseWidthClass="w-3.5"
                    baseHeightClass="h-1"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: (index + 3) * 0.1, duration: 0.8 }}
                  >
                    {/* ChatGPT on screen */}
                    <div className="absolute inset-0 flex items-center justify-center">
                      <img
                        src="/chatgpt.svg"
                        alt="ChatGPT"
                        className={`w-4 h-4 ${theme === 'dark' ? 'filter brightness-0 invert' : 'filter brightness-0'}`}
                      />
                    </div>
                  </Monitor>
                </div>

                {/* Cubicle divider (except after last monitor) */}
                {index < 2 && (
                  <div className="w-0.5 h-16 bg-gray-500/30 rounded-full mx-4"></div>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

      </div>
    </div>
  );
});

// Synkro Collaborative Features Component - Synkro with arrows to 6 connected PCs
const SynkroCollaborativeFeatures = memo(({ isMobile }: { isMobile: boolean }) => {
  const { theme } = useTheme();

  return (
    <div className={`relative ${isMobile ? 'w-80 h-80' : 'w-96 h-96'} flex flex-col items-center justify-center`}>
      {/* Synkro icon at top */}
      <div className="absolute top-8 left-0 right-0 flex justify-center">
        <div className="relative">
          <div
            className="w-8 h-8 rounded-lg border backdrop-blur-sm flex items-center justify-center relative transition-all duration-300 ring-2 ring-white/50 shadow-lg shadow-white/25 scale-110"
            style={{
              backgroundColor: 'rgba(147, 51, 234, 0.15)',
              borderColor: '#ffffff',
              boxShadow: '0 0 20px rgba(147, 51, 234, 0.5)'
            }}
          >
            <img
              src="/synkro.png"
              alt="Synkro"
              className={`w-6 h-6 ${theme === 'dark' ? 'filter brightness-0 invert' : 'filter brightness-0'}`}
            />
          </div>
        </div>
      </div>

      {/* Arrows pointing from Synkro to each monitor */}
      <div className="absolute top-14 left-0 right-0 flex justify-center">
        <div className="relative w-full h-16">
          <svg
            width="400"
            height="120"
            viewBox="0 0 400 120"
            className="absolute left-1/2 top-0 transform -translate-x-1/2"
          >
            <defs>
              <marker
                id="arrowhead"
                markerWidth="4"
                markerHeight="3"
                refX="3"
                refY="1.5"
                orient="auto"
              >
                <polygon
                  points="0 0, 4 1.5, 0 3"
                  fill="url(#arrowGradient)"
                />
              </marker>
              <linearGradient id="arrowGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#a855f7" />
                <stop offset="100%" stopColor="#3b82f6" />
              </linearGradient>
            </defs>

            {/* Arrows from center (Synkro) to each monitor position */}
            {[
              { startX: 200, startY: 15, endX: 100, endY: 105 }, // Monitor 1
              { startX: 200, startY: 15, endX: 140, endY: 105 }, // Monitor 2
              { startX: 200, startY: 15, endX: 180, endY: 105 }, // Monitor 3
              { startX: 200, startY: 15, endX: 220, endY: 105 }, // Monitor 4
              { startX: 200, startY: 15, endX: 260, endY: 105 }, // Monitor 5
              { startX: 200, startY: 15, endX: 300, endY: 105 }  // Monitor 6
            ].map((arrow, index) => (
              <motion.line
                key={index}
                x1={arrow.startX}
                y1={arrow.startY}
                x2={arrow.endX}
                y2={arrow.endY}
                stroke="url(#arrowGradient)"
                strokeWidth="2"
                markerEnd="url(#arrowhead)"
                className="drop-shadow-lg"
                initial={{ pathLength: 0, opacity: 0 }}
                animate={{ pathLength: 1, opacity: 1 }}
                transition={{
                  delay: 0.5 + index * 0.1,
                  duration: 0.8,
                  ease: "easeOut"
                }}
              />
            ))}
          </svg>
        </div>
      </div>

      {/* 6 connected computer monitors in a row */}
      <div className="relative mt-2 flex gap-2">
        {Array.from({ length: 6 }).map((_, index) => (
          <motion.div
            key={index}
            className="flex flex-col items-center"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.7 + index * 0.1, duration: 0.8 }}
          >
            <Monitor
              width="w-8"
              height="h-6"
              standWidthClass="w-2.5"
              standHeightClass="h-1"
              standNeckWidthClass="w-2"
              standNeckHeightClass="h-0.5"
              baseWidthClass="w-4"
              baseHeightClass="h-1"
            >
              {/* Synkro on screen */}
              <div className="absolute inset-0 flex items-center justify-center">
                <img
                  src="/synkro.png"
                  alt="Synkro"
                  className={`w-4 h-4 ${theme === 'dark' ? 'filter brightness-0 invert' : 'filter brightness-0'}`}
                />
              </div>
            </Monitor>
          </motion.div>
        ))}
      </div>

    </div>
  );
});

const AnimatedHeroCard = memo(({ isMobile }: { isMobile: boolean }) => {
  const [currentRotation, setCurrentRotation] = React.useState(0);
  const { theme } = useTheme();

  // Organized by quadrants with specific app categories
  const bands = useStableMemo(() => [
    {
      gradient: 'url(#band1)',
      icons: ['/trello.svg', '/jira.svg', '/asana.svg'],
      rotate: 0
    },
    {
      gradient: 'url(#band2)',
      icons: ['/whatsapp.svg', '/email.svg', '/salesforce.svg'],
      rotate: 90
    },
    {
      gradient: 'url(#band3)',
      icons: ['/googlecalendar.svg', '/gmail.svg', '/timer.svg'],
      rotate: 180
    },
    {
      gradient: 'url(#band4)',
      icons: ['/chatgpt.svg', '/excel.svg', '/quillbot.svg'],
      rotate: 270
    },
  ], [], 'hero-bands');

  return (
    <div className={`relative flex items-center justify-center ${isMobile ? 'w-[22rem] h-[22rem] max-w-[calc(100vw-2rem)] max-h-[calc(100vw-2rem)] translate-x-0' : 'w-[32rem] h-[32rem] translate-x-8'}`}>
      <motion.svg
        width="100%"
        height="100%"
        viewBox="0 0 600 600"
        className="absolute inset-0"
        initial={{ rotate: 0 }}
        animate={{ rotate: 360 }}
        transition={{ repeat: Infinity, duration: 20, ease: 'linear' }}
        onUpdate={(latest) => {
          setCurrentRotation(typeof latest.rotate === 'number' ? latest.rotate : 0);
        }}
      >
        <defs>
          <linearGradient id="band1" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#7c3aed" />
            <stop offset="100%" stopColor="#4338ca" />
          </linearGradient>
          <linearGradient id="band2" x1="100%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#4338ca" />
            <stop offset="100%" stopColor="#2563eb" />
          </linearGradient>
          <linearGradient id="band3" x1="0%" y1="100%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#2563eb" />
            <stop offset="100%" stopColor="#6366f1" />
          </linearGradient>
          <linearGradient id="band4" x1="100%" y1="100%" x2="0%" y2="0%">
            <stop offset="0%" stopColor="#6366f1" />
            <stop offset="100%" stopColor="#7c3aed" />
          </linearGradient>

        </defs>

        {bands.map((band, i) => (
          <g key={i} transform={`rotate(${band.rotate} 300 300)`}>
            {/* Simple curved band */}
            <path
              d="M300,70 A230,230 0 0,1 530,300 L460,300 A160,160 0 0,0 300,140 Z"
              fill={band.gradient}
              opacity="0.8"
            />
            {/* App icons with counter-rotation to stay upright */}
            {band.icons.map((iconSrc, iconIndex) => {
              // Calculate positions along the curved band
              const angle = (iconIndex + 1) * 25; // 25, 50, 75 degrees within the 90-degree band
              const radius = 195; // Between inner and outer radius
              const x = 300 + radius * Math.cos((angle - 90) * Math.PI / 180);
              const y = 300 + radius * Math.sin((angle - 90) * Math.PI / 180);

              return (
                <g key={iconIndex} transform={`translate(${x}, ${y})`}>
                  {/* Counter-rotation to keep icon upright - negative of total rotation */}
                  <g transform={`rotate(${-(currentRotation + band.rotate)})`}>
                    <image
                      x="-12"
                      y="-12"
                      width="24"
                      height="24"
                      href={iconSrc}
                      style={{
                        filter: theme === 'dark' ? 'brightness(0) invert(1)' : 'brightness(0)'
                      }}
                    />
                  </g>
                </g>
              );
            })}
          </g>
        ))}
      </motion.svg>


      {/* Center logo */}
      <div className={`relative z-10 bg-white/90 dark:bg-gray-900/90 rounded-full shadow-lg backdrop-blur-sm ${isMobile ? 'p-6' : 'p-10'}`}>
        <img src="/synkro.png" alt="Synkro Logo" className={`object-contain ${isMobile ? 'w-20 h-20' : 'w-32 h-32'}`} />
      </div>
    </div>
  );
});


const Landing = () => {
  // Section refs
  const heroRef = useRef(null);
  const demoRef = useRef(null);
  const navigate = useNavigate();
  const { isMobile } = useIsMobile();
  const { theme } = useTheme();

  // InView hooks for each section
  const heroInView = useInView(heroRef, { amount: 0.2, once: true });

  // Smooth scroll to demo section
  const scrollToDemo = useStableCallback(() => {
    demoRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'center'
    });
  }, []);

  return (
    <div className="h-full w-full text-foreground overflow-hidden transition-colors duration-300 landing-gradient-bg">


      {/* Hero Section */}
      <section ref={heroRef} className={`relative flex landing-hero ${isMobile ? 'items-start justify-center min-h-[100dvh] pt-6' : 'items-center justify-center min-h-screen'}`}>

        <div className={`relative z-10 mx-auto ${isMobile ? 'px-4 pt-16' : 'max-w-screen-2xl px-8 md:px-16 xl:px-32 2xl:px-40 pt-20'}`}>
          <div className={`grid items-center ${isMobile ? 'grid-cols-1 gap-8' : 'lg:grid-cols-2 gap-12'}`}>
            {/* Left side - Content */}
            <motion.div
              variants={fadeInUp}
              initial="hidden"
              animate={heroInView ? 'visible' : 'hidden'}
              className={isMobile ? 'text-center' : 'text-left'}
            >
              <h1 className={`font-bold leading-tight mb-6 text-slate-900 dark:text-white ${isMobile ? 'text-4xl sm:text-5xl text-center max-w-2xl mx-auto' : 'text-4xl md:text-5xl lg:text-6xl text-left'}`}>
                The Future of <span className="text-slate-900 dark:text-white">Consulting</span> is Here
              </h1>
              {!isMobile && (
                <p className="text-black dark:text-gray-400 mb-8 leading-relaxed text-xl">
                  Unify research, project management, and client collaboration in one platform.
                </p>
              )}

              {!isMobile && (
                <div className="flex gap-4 items-start flex-col sm:flex-row">
                  <Button
                    size="lg"
                    className="relative bg-black/10 dark:bg-white/10 backdrop-blur-md hover:bg-black/20 dark:hover:bg-white/20 border border-black/30 dark:border-white/30 text-black dark:text-white hover:text-black dark:hover:text-white drop-shadow-lg dark:drop-shadow-none transition-all duration-300 hover:scale-105 hover:shadow-lg hover:shadow-black/20 dark:hover:shadow-white/20 overflow-hidden group px-8 py-3 text-lg"
                    onClick={() => navigate('/auth')}
                  >
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700 ease-out" />
                    <span className="relative z-10 font-medium flex items-center">
                      Get Started
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </span>
                  </Button>
                  <Button
                    variant="ghost"
                    size="lg"
                    className="text-black dark:text-white hover:text-black dark:hover:text-white transition-all duration-300 hover:scale-105 px-8 py-3 text-lg"
                    onClick={scrollToDemo}
                  >
                    <span className="font-medium flex items-center">
                      <Play className="mr-2 h-5 w-5" />
                      See how it works
                    </span>
                  </Button>
                </div>
              )}
            </motion.div>

            {/* Right side - Animated Circle */}
            <motion.div
              variants={fadeInUpStagger(0.2)}
              initial="hidden"
              animate={heroInView ? 'visible' : 'hidden'}
              className={`flex ${isMobile ? 'justify-center -mt-4' : 'justify-center lg:justify-end -mt-8'}`}
            >
              <div className="flex flex-col items-center">
                <AnimatedHeroCard isMobile={isMobile} />
                {isMobile && (
                  <motion.div
                    variants={fadeInUpStagger(0.4)}
                    initial="hidden"
                    animate={heroInView ? 'visible' : 'hidden'}
                    className="mt-8 w-full"
                  >
                    <Button
                      size="lg"
                      className="relative bg-black/10 dark:bg-white/10 backdrop-blur-md hover:bg-black/20 dark:hover:bg-white/20 border border-black/30 dark:border-white/30 text-black dark:text-white hover:text-black dark:hover:text-white drop-shadow-lg dark:drop-shadow-none transition-all duration-300 hover:scale-105 hover:shadow-lg hover:shadow-black/20 dark:hover:shadow-white/20 overflow-hidden group px-8 py-3 text-lg w-full"
                      onClick={() => navigate('/auth')}
                    >
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700 ease-out" />
                      <span className="relative z-10 font-medium flex items-center justify-center">
                        Get Started
                        <ArrowRight className="ml-2 h-5 w-5" />
                      </span>
                    </Button>
                  </motion.div>
                )}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Fragmentation Problem Section */}
      <section className={`relative landing-section ${isMobile ? 'py-16' : 'py-24'}`}>
        <div className={`mx-auto ${isMobile ? 'px-4' : 'max-w-screen-2xl px-8 md:px-16 xl:px-32 2xl:px-40'}`}>
          <motion.div
            variants={fadeInUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            className="text-center mb-16"
          >
            <h2 className={`font-bold mb-6 ${isMobile ? 'text-3xl' : 'text-4xl lg:text-5xl'}`}>
              <span className="text-gray-900 dark:text-white">
                Fragmentation is the biggest problem in
              </span>
              <br />
              <span className="text-gray-900 dark:text-white">
                modern corporations
              </span>
            </h2>
            <p className={`text-black dark:text-gray-400 mx-auto ${isMobile ? 'text-base max-w-lg' : 'text-lg max-w-2xl'}`}>
              Teams waste countless hours switching between disconnected tools, losing context and productivity in the process.
            </p>
          </motion.div>

          {isMobile ? (
            <div className="flex flex-col items-center gap-8">
              <motion.div
                variants={fadeInUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
                className="flex flex-col items-center"
              >
                <FragmentedAppsAnimation isMobile={isMobile} />
              </motion.div>

              <div className="text-3xl text-purple-500 dark:text-white -translate-y-3">↓</div>

              <motion.div
                variants={fadeInUpStagger(0.2)}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
                className="flex flex-col items-center"
              >
                <SynkroUnifiedFeatures isMobile={isMobile} />
              </motion.div>
            </div>
          ) : (
            <div className={`grid items-center lg:grid-cols-2 gap-16 translate-x-4`}>
              <motion.div
                variants={fadeInUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
                className="flex flex-col items-center"
              >
                <FragmentedAppsAnimation isMobile={isMobile} />
              </motion.div>
              <motion.div
                variants={fadeInUpStagger(0.2)}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
                className="flex flex-col items-center"
              >
                <SynkroUnifiedFeatures isMobile={isMobile} />
              </motion.div>
            </div>
          )}

          {/* Arrow between panels */}
          <div className="absolute left-[52%] top-[60%] transform -translate-x-1/2 -translate-y-1/2 hidden lg:flex items-center justify-center z-10 pointer-events-none">
            <div className={`${theme === 'dark' ? 'text-white' : 'text-purple-500'} text-3xl font-bold`}>→</div>
          </div>
        </div>
      </section>

      {/* Collaboration Section */}
      <section className={`relative landing-section ${isMobile ? 'pt-8 pb-8' : 'pt-6 pb-12'}`}>
        <div className={`mx-auto ${isMobile ? 'px-4' : 'max-w-screen-2xl px-8 md:px-16 xl:px-32 2xl:px-40'}`}>
          <motion.div
            variants={fadeInUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            className="text-center mb-16"
          >
            <h2 className={`font-bold mb-6 ${isMobile ? 'text-3xl' : 'text-4xl lg:text-5xl'}`}>
              <span className="text-gray-900 dark:text-white">
                Less isolation, more collaboration
              </span>
            </h2>
            <p className={`text-black dark:text-gray-400 mx-auto ${isMobile ? 'text-base max-w-lg' : 'text-lg max-w-2xl'}`}>
              Break down silos and enable real-time collaboration across your entire team.
            </p>
          </motion.div>

          {isMobile ? (
            <div className="flex flex-col items-center gap-8">
              <motion.div
                variants={fadeInUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
                className="flex flex-col items-center"
              >
                <IsolatedCollaborationAnimation isMobile={isMobile} />
              </motion.div>

              <div className="text-3xl text-purple-500 dark:text-white -translate-y-3">↓</div>

              <motion.div
                variants={fadeInUpStagger(0.2)}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
                className="flex flex-col items-center"
              >
                <SynkroCollaborativeFeatures isMobile={isMobile} />
              </motion.div>
            </div>
          ) : (
            <div className="grid items-center lg:grid-cols-2 gap-16">
              <motion.div
                variants={fadeInUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
                className="flex flex-col items-center"
              >
                <IsolatedCollaborationAnimation isMobile={isMobile} />
              </motion.div>
              <motion.div
                variants={fadeInUpStagger(0.2)}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
                className="flex flex-col items-center"
              >
                <SynkroCollaborativeFeatures isMobile={isMobile} />
              </motion.div>
            </div>
          )}

          {/* Arrow between panels */}
          <div className="absolute left-[52%] top-1/2 transform -translate-x-1/2 -translate-y-1/2 hidden lg:flex items-center justify-center z-20 pointer-events-none">
            <div className={`${theme === 'dark' ? 'text-white' : 'text-purple-500'} text-3xl font-bold`}>→</div>
          </div>
        </div>
      </section>

      {/* See Synkro in Action (disabled for now — kept for future use) */}
      {false && (
        <section ref={demoRef} id="demo" className={`relative landing-section ${isMobile ? 'py-16' : 'pt-18 pb-24'}`}>
          <div className={`mx-auto ${isMobile ? 'px-4' : 'max-w-screen-2xl px-8 md:px-16 xl:px-32 2xl:px-40'}`}>
            <motion.div
              variants={fadeInUp}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.3 }}
              className={`text-center ${isMobile ? 'mb-12' : 'mb-16'}`}
            >
              <h2 className={`font-bold mb-4 ${isMobile ? 'text-3xl' : 'text-4xl'}`}>
                See Synkro in Action
              </h2>
              <p className={`text-black dark:text-gray-400 mx-auto ${isMobile ? 'text-base max-w-lg' : 'text-lg max-w-2xl'}`}>
                Watch how Synkro transforms research, project management, and team collaboration in one seamless platform.
              </p>
            </motion.div>

            <motion.div
              variants={fadeInUp}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.3 }}
              transition={{ delay: 0.2 }}
              className="relative"
            >
              <div className="relative aspect-video bg-gradient-to-br from-purple-50 to-blue-50 dark:from-purple-900/10 dark:to-blue-900/10 rounded-2xl overflow-hidden border border-gray-200 dark:border-gray-700 shadow-2xl z-10">
              </div>
            </motion.div>
          </div>

        </section>
      )}

      {/* Final CTA */}
      <section ref={demoRef} id="demo" className="relative landing-section !pt-12 !pb-24">
        <div className={`mx-auto ${isMobile ? 'px-4' : 'max-w-screen-2xl px-8 md:px-16 xl:px-32 2xl:px-40'}`}>
          <motion.div
            variants={fadeInUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            className="text-center"
          >
            <h2 className={`font-bold mb-4 ${isMobile ? 'text-3xl' : 'text-4xl lg:text-5xl'}`}>
              Try Synkro now
            </h2>
            <p className={`text-black dark:text-gray-400 mx-auto mb-10 ${isMobile ? 'text-base max-w-lg' : 'text-lg max-w-2xl'}`}>
              Start organizing your research, projects, and collaboration in one place — in minutes.
            </p>

            <div className="flex items-center justify-center gap-4 flex-col sm:flex-row">
              <Button
                size="lg"
                className="relative bg-black/10 dark:bg-white/10 backdrop-blur-md hover:bg-black/20 dark:hover:bg-white/20 border border-black/30 dark:border-white/30 text-black dark:text-white hover:text-black dark:hover:text-white drop-shadow-lg dark:drop-shadow-none transition-all duration-300 hover:scale-105 hover:shadow-lg hover:shadow-black/20 dark:hover:shadow-white/20 overflow-hidden group px-8 py-3 text-lg"
                onClick={() => navigate('/auth')}
              >
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700 ease-out" />
                <span className="relative z-10 font-medium flex items-center">
                  Get Started
                  <ArrowRight className="ml-2 h-5 w-5" />
                </span>
              </Button>

              <Button
                variant="ghost"
                size="lg"
                className="text-black dark:text-white hover:text-black dark:hover:text-white transition-all duration-300 hover:scale-105 px-8 py-3 text-lg"
                onClick={() => navigate('/pricing')}
              >
                <span className="font-medium">
                  View pricing
                </span>
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Landing; 